import React, { useState, useEffect, SetStateAction, Dispatch } from "react";
import { Button, Form, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import { TableResults, Utility } from "./FootageRestrictedLocateMaintenance";
import SelectModal from "./SelectModal";
import {
  Response,
  FootageRestrictedLocateConfig,
} from "./FootageRestrictedLocateMaintenance";
import "./ExpandedMenu.css";
import { getContractsByUtilityId } from './api';
import Select from 'react-select';
import SnackBar from '../../../../utilities/snackBar/SnackBar';

interface Props {
  isOpen: boolean;
  isEditMode: boolean;
  setIsEditMode: Dispatch<SetStateAction<boolean>>;
  onClose: () => void;
  onSave: (updatedConfig: FootageRestrictedLocateConfig) => void;
  onDisable: (Id: number) => void;
  onEnable: (Id: number) => void;
  selectedTableData: TableResults;
  allTblResponses?: Response[];
  allUtilities?: Utility[];
}

const ExpandedMenu = ({
  isOpen,
  isEditMode,
  setIsEditMode,
  onClose,
  onSave,
  onDisable,
  onEnable,
  selectedTableData,
  allTblResponses,
  allUtilities,
}: Props) => {
  const [showResponsesModal, setShowResponsesModal] = useState(false);
  const [showUtilitiesModal, setShowUtilitiesModal] = useState(false);
  const [selectedResponseProperty, setSelectedResponseProperty] = useState("");
  const [formData, setFormData] = useState<TableResults>(selectedTableData);
  const [contractsOptions, setContractsOptions] = useState<{value: number, label: string}[] | null>([]);
  const [selectedContractOption, setSelectedContractOption] = useState<{value: number, label: string} | null>();
  const [contractValidationMessage, setContractValidationMessage] = useState<string>('');

  const handleCancelClick = () => {
    setFormData(selectedTableData);
    setIsEditMode(false);

    // clear all contract data so when editing again the ui start fresh
    clearContractData();
  };

  // Save selected data locally so we can modify it for edits
  useEffect(() => {
    setFormData(selectedTableData);
  }, [selectedTableData]);

  // Shows a list of available responses to select from
  // ? Add search function to modal? Lots of options.
  const handleOpeningResponsesModal = (responseType: string) => {
    if (isEditMode) {
      setShowResponsesModal(true);
      setSelectedResponseProperty(responseType);
    }
  };

  const handleOpenUtilityModal = () => {
    if (isEditMode) {
      setShowUtilitiesModal(true);
    }
  };

  const handleSelectResponsesModal = (selectedResponse: Response) => {
    if (selectedResponse) {
      setFormData({
        ...formData,
        FootageRestrictedLocateConfig: {
          ...formData.FootageRestrictedLocateConfig,
          [selectedResponseProperty]: selectedResponse.ResponseID,
        },
      });
    }

    setShowResponsesModal(false);
  };

  const handleRemovingCurrentResponse = (selectedResponseType: string) => {
    if (selectedResponseType) {
      setFormData({
        ...formData,
        FootageRestrictedLocateConfig: {
          ...formData.FootageRestrictedLocateConfig,
          [selectedResponseType]: null,
        },
      });
    }
  };

  const handleSelectUtilityModal = (selectedUtility: Utility) => {
    if (selectedUtility) {
      getContracts(selectedUtility.UtilityID);            
        setFormData({
          ...formData,
          UtilityName: selectedUtility.UtilityName,
          FootageRestrictedLocateConfig: {
            ...formData.FootageRestrictedLocateConfig,
            UtilityId: selectedUtility.UtilityID,
          }
        });
    }

    setShowUtilitiesModal(false);
  };

  const getResponseByID = (responseID: number | null): Response => {
    // Empty response used to display nothing/blank spaces where needed
    const emptyResponse: Response = {
      ResponseID: 0,
      GUID: "",
      ResponseCode: "",
      ResponseText: "",
      ResponseUnitType: null,
      ModifyDate: "",
      Inactive: false,
      DateDeactivated: null,
      IsSystem: null,
    };

    return (
      allTblResponses?.find((response) => response.ResponseID === responseID) ??
      emptyResponse
    );
  };

  const handleDisableConfig = () => {
    onDisable(formData.FootageRestrictedLocateConfig.Id);

    setIsEditMode(false);
  };

  const handleEnableConfig = () => {
    onEnable(formData.FootageRestrictedLocateConfig.Id);

    //! currently shows that config was enabled even if the api fails
    setFormData({
      ...formData,
      FootageRestrictedLocateConfig: {
        ...formData.FootageRestrictedLocateConfig,
        IsActive: true,
      },
    });

    setIsEditMode(false);
  };

  const defaultResponse = getResponseByID(
    formData.FootageRestrictedLocateConfig.ResponseId
  );

  const hourlyResponse = getResponseByID(
    formData.FootageRestrictedLocateConfig.HourlyResponseID
  );

  const noBillResponse = getResponseByID(
    formData.FootageRestrictedLocateConfig.NoBillLocateID
  );

  const noBillHourlyResponse = getResponseByID(
    formData.FootageRestrictedLocateConfig.NoBillHourlyResponseID
  );

  const dateDeactivated = formData?.FootageRestrictedLocateConfig
    ?.DateDeactivated
    ? formData.FootageRestrictedLocateConfig.DateDeactivated.slice(0, 10)
    : null;

  const clearContractData = () => {
    setSelectedContractOption(null);
    setContractsOptions(null);
    setContractValidationMessage('');
  }

  const getContracts = async (utilityID: number) => {
    clearContractData();

    const response = await getContractsByUtilityId(utilityID);

    if (!response.HasError) {
      if (response.Data.length > 0) {
        const options = response.Data.map((contractData: { ContractID: number, CustomerName: string }) => {
          return { value: contractData.ContractID, label: contractData.CustomerName }
        });             

        if (formData.FootageRestrictedLocateConfig.ContractId > 0) {    // set existing contract to selected so data is in sync
          const existingContract = options.find((option: { value: number, label: string}) => option.value == formData.FootageRestrictedLocateConfig.ContractId);
          if (existingContract) {
            setSelectedContractOption(existingContract);
          }
        } else if (options.length == 1) {     // if no contract is selected already and there is only one option, auto set that option as selected
          setSelectedContractOption(options[0]);
        }

        setContractsOptions(options);
      } else {
        SnackBar({ text: 'No contracts exist for this utility.', type: 'error' })
        setContractValidationMessage('A contract is required to save a record.');
      }
    } else {
      SnackBar({ text: 'There was an error retrieving data from the server.'});
      setContractValidationMessage('An error occurred while retrieving data.');
    }   
  }

  const handleEditMode = () => {
    setIsEditMode(true);

    // Selected Utility 
    if (formData.FootageRestrictedLocateConfig.UtilityId > 0) {
      getContracts(formData.FootageRestrictedLocateConfig.UtilityId);
    }
  }

  const handleDisableEditButtons = () => {  
    if (contractsOptions && selectedContractOption) {
      return false;
    }

    return true;
  }

  // adds newly selected contractID to form data so that it can be saved
  useEffect(() => {
    if (selectedContractOption) {
      setFormData({
        ...formData,
        FootageRestrictedLocateConfig: {
          ...formData.FootageRestrictedLocateConfig,
          ContractId: selectedContractOption?.value
        }
      });
    }
  }, [selectedContractOption]);

  useEffect(() => {
    if (!isEditMode) {
      // on exit of 'edit' mode, clear all contract related useStates to be ready for next edit
      clearContractData();
    }
  }, [isEditMode]);

  return (
    <div
      className={`expanded-menu-menu-container-frlm ${
        isOpen ? "visible" : "closed"
      }`}
    >
      {/* Header */}
      <div
        style={{
          paddingLeft: "15px",
          paddingTop: "10px",
          paddingBottom: "10px",
          marginBottom: "6px",
          background: "#eaeaea",
        }}
      >
        <div>
          <h5
            style={{
              fontWeight: "bold",
            }}
          >
            {formData.UtilityName || "Assign Utility"}
            <span
              className={`expanded-menu-icon-edit-utility far edit-icon material-icons ${
                !isEditMode
                  ? "expanded-menu-table-row-icons-disabled-frlm"
                  : "expanded-menu-table-row-icons-enabled-frlm"
              }`}
              onClick={handleOpenUtilityModal}
            >
              edit
            </span>
          </h5>
          <div className="footage-restriction-expanded-menu-contract-id-container">
            Contract ID: {isEditMode ? (selectedContractOption ? selectedContractOption.value : '') : formData?.FootageRestrictedLocateConfig.ContractId}
            {contractValidationMessage.length > 0 && (
              <OverlayTrigger placement="right" overlay={<Tooltip id={`contract-validation`}>{contractValidationMessage}</Tooltip>}>
                  <span className="material-icons footage-restriction-expanded-menu-warning-icon">warning</span>
              </OverlayTrigger>
            )}
          </div>
          <div className="expanded-menu-disabled-date-text">
            {!formData.FootageRestrictedLocateConfig.IsActive
              ? `Inactive Since: ${dateDeactivated}`
              : " "}
          </div>
        </div>
        {isEditMode && (
          <div className="footage-restriction-expanded-menu-contract-dropdown-container">            
            <div className="footage-restriction-expanded-menu-contract-dropdown-wrapper">
              <Select
                options={contractsOptions}
                value={selectedContractOption}
                isDisabled={!contractsOptions || contractsOptions?.length <= 0}
                onChange={(selected: { value: number, label: string }) => {
                    setSelectedContractOption(selected);                                      
                }}
              />
            </div>  
            {formData.FootageRestrictedLocateConfig.UtilityId <= 0 && (
              <OverlayTrigger placement="right" overlay={<Tooltip id={`contract-validation`}>You must have a utility selected to select a contract.</Tooltip>}>
                  <span className="material-icons footage-restriction-expanded-menu-contract-info-icon">info</span>
              </OverlayTrigger>
            )}        
          </div>
        )}
        <span
          className="material-icons expanded-menu-close-icon"
          onClick={() => onClose()}
        >
          close
        </span>
      </div>

      <SelectModal<Response>
        isOpen={showResponsesModal}
        onRowSelect={handleSelectResponsesModal}
        onClose={() => setShowResponsesModal(false)}
        allItems={allTblResponses}
        // loadingStatus={loadingStatus}
        title="Select Response"
        renderRow={(response) => (
          <>
            <td>{response.ResponseCode}</td>
            <td>{response.ResponseText}</td>
            <td>{response.ResponseUnitType}</td>
          </>
        )}
        tableColumnHeaders={["Code", "Text", "Unit Type"]}
      />

      <SelectModal<Utility>
        isOpen={showUtilitiesModal}
        onRowSelect={handleSelectUtilityModal}
        onClose={() => setShowUtilitiesModal(false)}
        allItems={allUtilities}
        // loadingStatus={loadingStatus}
        title="Select Utility"
        renderRow={(utility) => (
          <>
            <td>{utility.UtilityAbbr}</td>
            <td>{utility.UtilityName}</td>
          </>
        )}
        tableColumnHeaders={["Utility Abbreviation", "Utility Name"]}
      />

      {/* Config Info */}
      <div className="expanded-menu-config-info">
        <Form>
          <Form.Row>
            <Form.Group as={Col}>
              <Form.Label>Overflow To Hourly</Form.Label>
              <Form.Control
                readOnly={!isEditMode}
                plaintext={!isEditMode}
                disabled={!isEditMode}
                as="select"
                value={
                  formData?.FootageRestrictedLocateConfig.OverflowToHourly
                    ? "True"
                    : "False"
                }
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    FootageRestrictedLocateConfig: {
                      ...formData.FootageRestrictedLocateConfig,
                      OverflowToHourly: e.target.value === "True",
                    },
                  })
                }
              >
                <option>True</option>
                <option>False</option>
              </Form.Control>
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label>No Plant Billable</Form.Label>
              <Form.Control
                readOnly={!isEditMode}
                plaintext={!isEditMode}
                disabled={!isEditMode}
                as="select"
                value={
                  formData?.FootageRestrictedLocateConfig.NoPlantBillable
                    ? "True"
                    : "False"
                }
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    FootageRestrictedLocateConfig: {
                      ...formData.FootageRestrictedLocateConfig,
                      NoPlantBillable: e.target.value === "True",
                    },
                  })
                }
              >
                <option>True</option>
                <option>False</option>
              </Form.Control>
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label>Hourly Minimum</Form.Label>
              <Form.Control
                plaintext={!isEditMode}
                readOnly={!isEditMode}
                value={
                  formData.FootageRestrictedLocateConfig.HourlyMinimum
                    ? formData.FootageRestrictedLocateConfig.HourlyMinimum
                    : 0
                }
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    FootageRestrictedLocateConfig: {
                      ...formData.FootageRestrictedLocateConfig,
                      HourlyMinimum: parseInt(e.target.value),
                    },
                  })
                }
              />
            </Form.Group>
          </Form.Row>

          {/* 2 */}
          <Form.Row>
            <Form.Group as={Col}>
              <Form.Label>Minimum Billable Minutes</Form.Label>
              <Form.Control
                plaintext={!isEditMode}
                readOnly={!isEditMode}
                value={
                  formData.FootageRestrictedLocateConfig.MinBillableMinutes
                    ? formData.FootageRestrictedLocateConfig.MinBillableMinutes
                    : 0
                }
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    FootageRestrictedLocateConfig: {
                      ...formData.FootageRestrictedLocateConfig,
                      MinBillableMinutes: parseInt(e.target.value),
                    },
                  })
                }
              />
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label>Max Locates</Form.Label>
              <Form.Control
                plaintext={!isEditMode}
                readOnly={!isEditMode}
                value={
                  formData.FootageRestrictedLocateConfig.MaxLocates
                    ? formData.FootageRestrictedLocateConfig.MaxLocates
                    : 0
                }
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    FootageRestrictedLocateConfig: {
                      ...formData.FootageRestrictedLocateConfig,
                      MaxLocates: parseInt(e.target.value),
                    },
                  })
                }
              />
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label>Max Footage</Form.Label>
              <Form.Control
                plaintext={!isEditMode}
                readOnly={!isEditMode}
                value={
                  formData.FootageRestrictedLocateConfig.MaxFootage
                    ? formData.FootageRestrictedLocateConfig.MaxFootage
                    : 0
                }
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    FootageRestrictedLocateConfig: {
                      ...formData.FootageRestrictedLocateConfig,
                      MaxFootage: parseInt(e.target.value),
                    },
                  })
                }
              />
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label>Footage Per Locate</Form.Label>
              <Form.Control
                plaintext={!isEditMode}
                readOnly={!isEditMode}
                value={
                  formData.FootageRestrictedLocateConfig.FootagePerLocate
                    ? formData.FootageRestrictedLocateConfig.FootagePerLocate
                    : 0
                }
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    FootageRestrictedLocateConfig: {
                      ...formData.FootageRestrictedLocateConfig,
                      FootagePerLocate: parseInt(e.target.value),
                    },
                  })
                }
              />
            </Form.Group>
          </Form.Row>

          {/* 3 */}
          <Table>
            <thead>
              <tr>
                <th>Responses</th>
                <th style={{ minWidth: "250px" }}>Response Text</th>
                <th>Code</th>
                <th>Unit Type</th>
                <th>Is System</th>
                <th style={{ width: "100px" }}></th>
              </tr>
            </thead>
            <tbody
              className={`${
                !isEditMode
                  ? "expanded-menu-table-disabled-frlm"
                  : "expanded-menu-table-enabled-frlm"
              }`}
            >
              <tr>
                <td>Default</td>
                <td>{defaultResponse.ResponseText}</td>
                <td>{defaultResponse.ResponseCode}</td>
                <td>{defaultResponse.ResponseUnitType || ""}</td>
                <td>{defaultResponse.IsSystem ? "True" : "False"}</td>
                <td>
                  <span
                    className={`expanded-menu-icon far edit-icon material-icons ${
                      !isEditMode
                        ? "expanded-menu-table-row-icons-disabled-frlm"
                        : "expanded-menu-table-row-icons-enabled-frlm"
                    }`}
                    onClick={() => handleOpeningResponsesModal("ResponseId")}
                  >
                    edit
                  </span>
                </td>
              </tr>
              <tr>
                <td>Hourly</td>
                <td>{hourlyResponse.ResponseText}</td>
                <td>{hourlyResponse.ResponseCode}</td>
                <td>{hourlyResponse.ResponseUnitType || ""}</td>
                <td>{hourlyResponse.IsSystem ? "True" : "False"}</td>
                <td>
                  <span
                    className={`expanded-menu-icon far edit-icon material-icons ${
                      !isEditMode
                        ? "expanded-menu-table-row-icons-disabled-frlm"
                        : "expanded-menu-table-row-icons-enabled-frlm"
                    }`}
                    onClick={() =>
                      handleOpeningResponsesModal("HourlyResponseID")
                    }
                  >
                    edit
                  </span>
                  <span
                    className={`expanded-menu-icon far edit-icon material-icons ${
                      !isEditMode
                        ? "expanded-menu-table-row-icons-disabled-frlm"
                        : "expanded-menu-table-row-icons-enabled-frlm"
                    }`}
                    onClick={() =>
                      handleRemovingCurrentResponse("HourlyResponseID")
                    }
                  >
                    delete
                  </span>
                </td>
              </tr>
              <tr>
                <td>No Bill</td>
                <td>{noBillResponse.ResponseText}</td>
                <td>{noBillResponse.ResponseCode}</td>
                <td>{noBillResponse.ResponseUnitType || ""}</td>
                <td>{noBillResponse.IsSystem ? "True" : "False"}</td>
                <td>
                  <span
                    className={`expanded-menu-icon far edit-icon material-icons ${
                      !isEditMode
                        ? "expanded-menu-table-row-icons-disabled-frlm"
                        : "expanded-menu-table-row-icons-enabled-frlm"
                    }`}
                    onClick={() =>
                      handleOpeningResponsesModal("NoBillLocateID")
                    }
                  >
                    edit
                  </span>
                </td>
              </tr>
              <tr>
                <td>No Bill Hourly</td>
                <td>{noBillHourlyResponse.ResponseText}</td>
                <td>{noBillHourlyResponse.ResponseCode}</td>
                <td>{noBillHourlyResponse.ResponseUnitType || ""}</td>
                <td>{noBillHourlyResponse.IsSystem ? "True" : "False"}</td>
                <td>
                  <span
                    className={`expanded-menu-icon far edit-icon material-icons ${
                      !isEditMode
                        ? "expanded-menu-table-row-icons-disabled-frlm"
                        : "expanded-menu-table-row-icons-enabled-frlm"
                    }`}
                    onClick={() =>
                      handleOpeningResponsesModal("NoBillHourlyResponseID")
                    }
                  >
                    edit
                  </span>
                  <span
                    className={`expanded-menu-icon far edit-icon material-icons ${
                      !isEditMode
                        ? "expanded-menu-table-row-icons-disabled-frlm"
                        : "expanded-menu-table-row-icons-enabled-frlm"
                    }`}
                    onClick={() =>
                      handleRemovingCurrentResponse("NoBillHourlyResponseID")
                    }
                  >
                    delete
                  </span>
                </td>
              </tr>
            </tbody>
          </Table>

          {/* 4 */}
          <Form.Row>
            <Form.Group
              as={Col}
              controlId="FootageRestrictedLocateConfigComments"
            >
              <Form.Label>Comments</Form.Label>
              <Form.Control
                plaintext={!isEditMode}
                as="textarea"
                readOnly={!isEditMode}
                value={formData.FootageRestrictedLocateConfig.Comments || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    FootageRestrictedLocateConfig: {
                      ...formData.FootageRestrictedLocateConfig,
                      Comments: e.target.value,
                    },
                  })
                }
              />
            </Form.Group>
          </Form.Row>
        </Form>

        {/* Buttons */}
        <div className="expanded-menu-config-buttons">
          {!isEditMode && (
            <Button variant="secondary" onClick={() => handleEditMode()}>
              Edit
            </Button>
          )}
          {isEditMode && (
            <>
              <Button
                variant={
                  formData.FootageRestrictedLocateConfig.IsActive
                    ? "danger"
                    : "success"
                }
                onClick={
                  formData.FootageRestrictedLocateConfig.IsActive
                    ? handleDisableConfig
                    : handleEnableConfig
                }
              >
                {formData.FootageRestrictedLocateConfig.IsActive
                  ? "Disable"
                  : "Re-Enable"}
              </Button>
              <Button variant="secondary" onClick={handleCancelClick}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => onSave(formData.FootageRestrictedLocateConfig)}
                disabled={handleDisableEditButtons()}
              >
                Save
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpandedMenu;
